import { useEffect, useState, lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation, Outlet } from 'react-router-dom';
import Loader from './common/Loader';
import PageTitle from './components/UiElements/PageTitle';
import ForgotPassword from './pages/Authentication/ForgotPassword';
import ResetPassword from './pages/Authentication/ResetPassword';
import { useUserStore } from './stores/userStore';
import { useTokenCheck } from './hooks/useTokenCheck';
import DefaultLayout from './layout/DefaultLayout';
import { NotificationProvider } from './providers/NotificationProvider';
import { SearchProvider } from './contexts/SearchContext';
import { ChatProvider } from './contexts/ChatContext';
import { Toaster } from 'react-hot-toast';
import { FloatingChat } from './components/Chat/Chat';
import FormPersistenceDebug from './components/Debug/FormPersistenceDebug';
import SignIn from './pages/Authentication/SignIn';
import TenantAccountNotice from './components/Auth/TenantAccountNotice';
import { DASHBOARD_HOME_ENABLED } from './config/features';

// Lazy load routes for better performance
// The new home is behind DASHBOARD_HOME_ENABLED (src/config/features.ts); the existing Overview stays the default.
const LazyOverview = lazy<ComponentType>(() =>
  DASHBOARD_HOME_ENABLED ? import('./pages/dashboard/DashboardHome') : import('./pages/Overview'),
);
const LazySchedule = lazy(() => import('./pages/Schedule'));
const LazyNotes = lazy(() => import('./pages/Notes'));
const LazyProposals = lazy(() => import('./pages/Proposals'));
const LazySettings = lazy(() => import('./pages/Settings'));
const LazyLeads = lazy(() => import('./pages/Leads'));
const LazyPlots = lazy(() => import('./pages/Plots'));
const LazyContracts = lazy(() => import('./pages/Contracts'));
const LazyCrops = lazy(() => import('./pages/Crops'));
const LazyContacts = lazy(() => import('./pages/Contacts'));
const LazyInvoices = lazy(() => import('./pages/Invoices'));
const LazyPayments = lazy(() => import('./pages/Payments'));
const LazyArrears = lazy(() => import('./pages/Arrears'));
const LazyTaxReporting = lazy(() => import('./pages/TaxReporting'));
const LazyCropPrices = lazy(() => import('./components/CropPrices/CropPriceManager'));

// Types
interface RouteConfig {
  path: string;
  title: string;
  element: React.ReactNode;
}

// Page wrapper component for consistent layout and title
const Page = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Suspense fallback={<Loader />}>
    <PageTitle title={title} />
    {children}
  </Suspense>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { token, currentUser, isLoading, checkAuth } = useUserStore();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        if (token) {
          const isAuthenticated = await checkAuth();
          if (!isAuthenticated) {
       //     console.log('ProtectedRoute - Authentication failed, redirecting to login');
            // The checkAuth function will handle the logout and token clearing
          }
        } else {
       //   console.log('ProtectedRoute - No token found, not authenticated');
        }
      } catch (error) {
    //    console.error('ProtectedRoute - Error during auth check:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    verifyAuth();
  }, [token, checkAuth]);
  
  
  // Show loading state while checking auth
  if (isLoading || isCheckingAuth) {
    return <Loader />;
  }
  
  // If there's no token or no user, redirect to login
  if (!token || !currentUser) {
   // console.log('ProtectedRoute - Not authenticated, redirecting to login');
    return <Navigate to="/auth/signin" state={{ from: location }} replace />;
  }
  
  // A tenant session opened in the landlord app: explain, don't show a wall of 403s
  if (currentUser.role === ('tenant' as string)) {
    return <TenantAccountNotice />;
  }

  // If we have both token and user, render the protected content
 // console.log('ProtectedRoute - User authenticated, rendering protected content');
  return <>{children}</>;
};

const Layout = () => (
  <DefaultLayout>
    <Outlet />
  </DefaultLayout>
);

// Page components with Suspense
const OverviewPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyOverview />
  </Suspense>
);

const SchedulePage = () => (
  <Suspense fallback={<Loader />}>
    <LazySchedule />
  </Suspense>
);

const ProposalsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyProposals />
  </Suspense>
);

const NotesPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyNotes />
  </Suspense>
);




const PlotsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyPlots />
  </Suspense>
);



const SettingsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazySettings />
  </Suspense>
);

const ContactsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyContacts />
  </Suspense>
);

const ContractsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyContracts />
  </Suspense>
);

const InvoicesPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyInvoices />
  </Suspense>
);

const PaymentsPage = () => (
  <Suspense fallback={<Loader />}>
    <LazyPayments />
  </Suspense>
);

const ActivityPage = () => (
  <Suspense fallback={<Loader />}>
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Activity</h1>
      <p>Activity tracking has been moved to the Schedule page.</p>
    </div>
  </Suspense>
);

// Route configuration


const routes: RouteConfig[] = [
  {
    path: '/leads',
    title: 'Leads',
    element: (
      <Page title="Leads">
        <LazyLeads />
      </Page>
    ),
  },
  {
    path: '/leads/:id',
    title: 'Lead Details',
    element: (
      <Page title="Lead Details">
        <LazyLeads />
      </Page>
    ),
  },
  {
    path: '/dashboard',
    title: 'Dashboard',
    element: <OverviewPage />
  },
  {
    path: '/schedule',
    title: 'Schedule',
    element: <SchedulePage />
  },
  {
    path: '/proposals',
    title: 'Proposals',
    element: <ProposalsPage />
  },
  {
    path: '/notes',
    title: 'Notes',
    element: <NotesPage />
  },
  {
    path: '/activity',
    title: 'Activity',
    element: <ActivityPage />
  },
  {
    path: '/plots',
    title: 'Plots',
    element: <PlotsPage />
  },
  {
    path: '/plots/:id',
    title: 'Plots',
    element: <PlotsPage />
  },
  // Settings route is defined in the Routes component below
  {
    path: '/contacts',
    title: 'Contacts',
    element: <ContactsPage />
  },
  {
    path: '/contracts',
    title: 'Contracts',
    element: <ContractsPage />
  },
  {
    path: '/invoices',
    title: 'Invoices',
    element: <InvoicesPage />
  },
  {
    path: '/invoices/arrears',
    title: 'Arrears',
    element: (
      <Page title="Arrears">
        <LazyArrears />
      </Page>
    )
  },

  {
    path: '/payments',
    title: 'Payments',
    element: <PaymentsPage />
  },
  {
    path: '/tax-reporting',
    title: 'Tax Reporting',
    element: (
      <Page title="Tax Reporting">
        <LazyTaxReporting />
      </Page>
    )
  },
  {
    path: '/settings',
    title: 'Settings',
    element: <SettingsPage />
  },
  {
    path: '/crops',
    title: 'Crops',
    element: (
      <Page title="Crops">
        <LazyCrops />
      </Page>
    )
  },
  {
    path: '/crop-prices',
    title: 'Crop Prices',
    element: (
      <Page title="Crop Prices">
        <LazyCropPrices />
      </Page>
    )
  }
 
];

function App() {
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();
  const { token, checkAuth } = useUserStore();
  
  // Use the token check hook for periodic checks
  useTokenCheck();

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Always check authentication on page load if there's a token
        // This ensures we restore user state on page refresh
        if (token) {
          await checkAuth();
        } else {
          console.log('No token found, skipping authentication check');
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
      } finally {
        setLoading(false);
      }
    };
  
    initializeAuth();
  }, [token, checkAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (loading) {
    return <Loader />;
  }

  return (
    <ChatProvider>
      <SearchProvider>
        <NotificationProvider>
          <Toaster
            position="top-center"
            containerStyle={{
              top: '16px',
              bottom: 'auto',
              left: '16px',
              right: '16px'
            }}
            toastOptions={{
              duration: 5000,
              style: {
                background: '#DC2626',
                color: '#FFFFFF',
                padding: '16px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                maxWidth: 'min(500px, calc(100vw - 32px))',
                width: 'fit-content',
                wordBreak: 'break-word',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              className: 'custom-toast',
              success: {
                duration: 5000,
                style: {
                  background: '#10B981'
                }
              },
              error: {
                duration: 5000,
                style: {
                  background: '#DC2626'
                }
              },
              iconTheme: {
                primary: '#FFFFFF',
                secondary: '#10B981',
              }
            }}
          />
          
          {/* Render FloatingChat when chat is open */}
          <FloatingChat />
          
          <Routes>
            {/* Public Routes */}
            <Route path="/auth/signin" element={<SignIn />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            
            {/* Protected Routes */}
            <Route element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              
              {/* Map all routes */}
              {routes.map(({ path, title, element }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <Page title={title}>
                      {element}
                    </Page>
                  }
                />
              ))}
              
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
          {/* Development debugging component */}
          {import.meta.env.DEV && <FormPersistenceDebug />}
        </NotificationProvider>
      </SearchProvider>
    </ChatProvider>
  );
}

export default App;
